/**
 * Task rows and the grouped list, with the §8.4 completion animation.
 *
 * The animation runs on the row that was ticked and only after the tick — it is
 * voluntary motion, triggered by the user, which §8.4 says is the kind worth having.
 * Nothing animates on load.
 *
 * Grouping folds §8.5's slipping view into the list you actually work from. Seeing that
 * something is slipping in a separate panel means finding it again in the main list before
 * you can do anything about it, and that hunt is exactly the executive function this app
 * exists to spare you.
 */

import { useEffect, useState } from "react";
import type { Area, Project, RecurrenceRequest, Task, Tier } from "../api";
import { type AiTierAvailability, NO_AI_TIERS, useAiTierAvailability } from "../aiTiers";
import { ClampedText } from "./ClampedText";
import { TaskEditor, type TaskPatch } from "./TaskEditor";
import { BreakdownForm, SubtaskFold, type UndoableBreakdown } from "./Subtasks";
import { AiBreakdownButton } from "./AiBreakdownButton";
import { EnergyBadgeButton } from "./EnergyBadgeButton";
import { TaskGlow } from "./TaskGlow";
import { Die } from "./Die";
import { energyTitle, estimateLabel, isOverdue, recurrenceLabel, relativeDateLabel } from "./taskFormat";
import { Calendar, Chevron, Important, Pencil, Repeat, Snooze, Split, Star, Stopwatch, Trash } from "./icons";
import { OverflowMenu, type OverflowMenuItem } from "./OverflowMenu";
import { useSettings } from "../settings";

export type TaskGroup = "overdue" | "important" | "slipping" | "open";

const GROUP_LABELS: Record<TaskGroup, { title: string; blurb: string }> = {
  overdue: { title: "Overdue and Today", blurb: "Due today or already passed" },
  important: { title: "Important", blurb: "Matters whatever the slider says" },
  slipping: { title: "Slipping", blurb: "Untouched past its threshold" },
  open: { title: "Everything else", blurb: "" },
};

const GROUP_ORDER: TaskGroup[] = ["overdue", "important", "slipping", "open"];

/**
 * Per-group sorting, within `GroupedTaskList` only (the one card with these four headings)
 * — each group keeps its own sort, independent of the other three, since "Overdue" and
 * "Everything else" are different enough lists that reading one by due date has no bearing
 * on wanting the other sorted by how long it's been quiet.
 *
 * Same order `TaskRow` actually renders its badges in — energy, then area, due, estimate,
 * chosen, quiet, seen, pushed (§11's skim-order pass: energy first as the fixed-width
 * scan target, chosen/quiet/seen swapped from their old order to match) — not the order
 * they happened to come up in conversation. "Project" isn't here: it never gets a badge of
 * its own on the row (only area does, area's own tooltip names the project when there is
 * one), so there was nothing to sort *by* — which also frees "P" back up for "Pushed", its
 * natural letter. Energy has no letter at all; its own button is a die instead
 * (`TaskSortControls`), flipping between showing 1 and 5 as the direction toggles — the same
 * thing ascending/descending already means for every other field.
 *
 * Hidden entirely in Execute mode, sort buttons and all (`settings.cardMode`) — sorting is
 * a planning activity, and a sort button for a badge Execute mode has also hidden would be
 * exactly the "sortable but invisible" confusion a global toggle exists to avoid.
 */
export type TaskSortKey =
  | "area"
  | "energy"
  | "due"
  | "estimate"
  | "quiet"
  | "seen"
  | "chosen"
  | "pushed";

export type TaskSort = { key: TaskSortKey; direction: "asc" | "desc" };

/** What a fresh group starts sorted by — `last_touched_at ASC`, the server's own order
 * (`OPEN_TASK_FILTER`), restated in terms of a field this list can actually toggle: the
 * *most* quiet days first is the same set of rows the server's "oldest touched first" would
 * have put first. There's no unsorted state to fall back to instead — every group is always
 * sorted by *something*, this is just what it starts as. */
const INITIAL_SORT: TaskSort = { key: "quiet", direction: "desc" };

/**
 * The question each sort answers — a second tooltip line, so the letter doesn't have to be
 * memorised to be useful. Reworded to a consistent first-person-question voice; the original
 * asks ("Area -> Grouped by theme", "Die -> how much energy do I have", …) were a mix of a
 * description and a handful of different sentence shapes.
 */
const SORT_FIELDS: {
  key: Exclude<TaskSortKey, "energy">;
  letter: string;
  label: string;
  hint: string;
}[] = [
  { key: "area", letter: "A", label: "Area", hint: "What's grouped by the same theme?" },
  { key: "due", letter: "D", label: "Due", hint: "What's coming up I should stay ahead of?" },
  { key: "estimate", letter: "E", label: "Estimate", hint: "Is there something quick I can knock out?" },
  { key: "chosen", letter: "C", label: "Chosen", hint: "What have I been wanting to do?" },
  { key: "quiet", letter: "Q", label: "Quiet", hint: "What might I want to get back to?" },
  { key: "seen", letter: "S", label: "Seen", hint: "What's been on my list a while?" },
  { key: "pushed", letter: "P", label: "Pushed", hint: "What am I avoiding?" },
];

const ENERGY_HINT = "How much energy do I have?";

/**
 * The direction a field starts in the first time it becomes the active sort. Not uniformly
 * ascending: Energy/Area/Due/Estimate answer "what's smallest/soonest/cheapest," so
 * ascending puts the most useful row first; Chosen/Quiet/Seen/Pushed answer "what's
 * happened to this the most," so it's descending that leads with the most, not the least.
 */
const DEFAULT_SORT_DIRECTION: Record<TaskSortKey, "asc" | "desc"> = {
  energy: "asc",
  area: "asc",
  due: "asc",
  estimate: "asc",
  chosen: "desc",
  quiet: "desc",
  seen: "desc",
  pushed: "desc",
};

/**
 * A parent's own energy isn't what decides whether it's worth starting — its Next Action is
 * (§8.6.1). A big, folded project whose next open step only costs 2 has no business sorting
 * behind a flat 5, so a task with subtasks sorts on its Next Action's energy instead of its
 * own. Falls back to the task's own energy when there's no subtask or no Next Action set
 * (everything under it done, say) — same as an ordinary task. Unset energy, on either the
 * task or its Next Action, is treated as `0`: the cheapest there is, not a value that gets
 * shuffled to one end regardless of direction the way a missing due date does.
 */
function energySortValue(task: Task): number {
  const nextAction =
    task.next_action_id && task.subtasks
      ? task.subtasks.find((s) => s.id === task.next_action_id)
      : undefined;
  return (nextAction ?? task).energy ?? 0;
}

/** `null` sorts last regardless of direction — only `area`/`due` can still produce it
 * (a task with no due date, or fetched from an endpoint that didn't join area/project).
 * Every other field already resolves its own "nothing here" case to `0` before this ever
 * sees it (`energySortValue`, and `sortValue`'s own `?? 0` below) — a task with no estimate
 * or no history on a field reads as *having none*, which is a real, comparable value, not
 * an absence that should jump to one end the way an unanswerable "when is this due" does. */
function sortValue(task: Task, key: TaskSortKey): string | number | null {
  switch (key) {
    case "area":
      return task.area_name ?? null;
    case "energy":
      return energySortValue(task);
    case "due":
      return task.due;
    case "estimate":
      return task.estimate_min ?? 0;
    case "quiet":
      return task.days_since_touch ?? 0;
    case "seen":
      return task.surfaced_count;
    case "chosen":
      return task.starred_count;
    case "pushed":
      return task.deferred_count;
  }
}

function compareTasks(a: Task, b: Task, sort: TaskSort): number {
  const va = sortValue(a, sort.key);
  const vb = sortValue(b, sort.key);
  if (va === null && vb === null) return 0;
  if (va === null) return 1; // missing values last, whichever direction
  if (vb === null) return -1;
  const cmp = typeof va === "string" && typeof vb === "string" ? va.localeCompare(vb) : va < vb ? -1 : va > vb ? 1 : 0;
  return sort.direction === "asc" ? cmp : -cmp;
}

/** Picking a new field starts it at *that field's own* default direction
 * (`DEFAULT_SORT_DIRECTION`), not uniformly ascending — direction belongs to "this field,
 * right now," not to a mode that persists across fields. Picking the field that's already
 * active just flips it; there is no third state, since a group is never *not* sorted by
 * something (`INITIAL_SORT`). */
function nextSort(current: TaskSort, key: TaskSortKey): TaskSort {
  if (current.key !== key) return { key, direction: DEFAULT_SORT_DIRECTION[key] };
  return { key, direction: current.direction === "asc" ? "desc" : "asc" };
}

/**
 * One button per field, in `TaskRow`'s own badge order, plus the energy die where the
 * energy badge itself sits. Every button always shows a letter (or the die) and a
 * direction glyph — the only thing that changes when a field becomes the active sort is
 * the background (`.task-sort-btn.active`, the row colour against the heading's darker
 * one), not which glyphs exist. An inactive button shows *that field's own* default
 * direction's glyph (`DEFAULT_SORT_DIRECTION`), since that's what clicking it will produce
 * — Chosen/Quiet/Seen/Pushed preview the descending arrow while inactive, not ascending.
 */
export function TaskSortControls({
  sort,
  onChange,
}: {
  sort: TaskSort;
  onChange: (next: TaskSort) => void;
}) {
  function fieldButton(key: Exclude<TaskSortKey, "energy">, letter: string, label: string, hint: string) {
    const active = sort.key === key;
    const ascending = active ? sort.direction === "asc" : DEFAULT_SORT_DIRECTION[key] === "asc";
    return (
      <button
        key={key}
        type="button"
        className={`task-sort-btn${active ? " active" : ""}`}
        title={`Sort by ${label}\n${hint}`}
        aria-label={`Sort by ${label}`}
        aria-pressed={active}
        onClick={() => onChange(nextSort(sort, key))}
      >
        {letter}
        <span className={`task-sort-arrow${ascending ? " asc" : ""}`}>
          <Chevron size={12} />
        </span>
      </button>
    );
  }

  const [area, due, estimate, chosen, quiet, seen, pushed] = SORT_FIELDS;

  return (
    <span className="task-sort" role="group" aria-label="Sort">
      <button
        type="button"
        className={`task-sort-btn${sort.key === "energy" ? " active" : ""}`}
        title={`Sort by Energy\n${ENERGY_HINT}`}
        aria-label="Sort by Energy"
        aria-pressed={sort.key === "energy"}
        onClick={() => onChange(nextSort(sort, "energy"))}
      >
        <Die value={sort.key === "energy" && sort.direction === "desc" ? 5 : 1} />
      </button>
      {fieldButton(area.key, area.letter, area.label, area.hint)}
      {fieldButton(due.key, due.letter, due.label, due.hint)}
      {fieldButton(estimate.key, estimate.letter, estimate.label, estimate.hint)}
      {fieldButton(chosen.key, chosen.letter, chosen.label, chosen.hint)}
      {fieldButton(quiet.key, quiet.letter, quiet.label, quiet.hint)}
      {fieldButton(seen.key, seen.letter, seen.label, seen.hint)}
      {fieldButton(pushed.key, pushed.letter, pushed.label, pushed.hint)}
    </span>
  );
}

export type TaskActions = {
  onComplete: (task: Task, at?: { x: number; y: number }) => void | Promise<void>;
  onDefer?: (task: Task) => void | Promise<void>;
  onStar?: (task: Task) => void | Promise<void>;
  onEdit?: (
    task: Task,
    patch: TaskPatch,
    recurrence?: RecurrenceRequest,
  ) => Promise<void>;
  /**
   * Drop a task: §7's third status, alongside open and done.
   *
   * Not a hard delete, and deliberately so. `events` references tasks by id, so removing
   * the row would leave completions in the history pointing at nothing — old wins would
   * render as "(untitled)" and the §8.5 queries would read a hole. PARA archives rather
   * than deletes (§4.1 makes that a status, not a table), and this follows it.
   */
  onDrop?: (task: Task) => void | Promise<void>;
  /** Permanent removal, for things that should not exist rather than not be done. */
  onDelete?: (task: Task) => Promise<void>;
  /**
   * Put a completed task back.
   *
   * Completing removes a task from every list it was in, so a misclick used to be
   * unrecoverable — there is no completed-tasks view to go and un-tick it from. A ticked
   * row therefore stays put until the page is next loaded, and this is how it comes back.
   */
  onUndo?: (task: Task) => void | Promise<void>;
  /**
   * Break a task down (§8.6). On a task with no parent this creates children; called on a
   * subtask instead, the server treats it as a split (§8.6.2, §10) — one control for both,
   * so this callback never needs to know which of the two it was given.
   */
  onBreakdown?: (task: Task, titles: string[]) => Promise<Task[] | void>;
  /**
   * AI breakdown (§8.7, §11 item 9b) — same parent-vs-split dispatch as `onBreakdown`, a
   * model call instead of the titles the owner already typed. Online-only: there's no
   * queueable shape for "ask a model" the way there is for a titles list (§8.2, D157).
   */
  onAiBreakdown?: (task: Task, tier: Tier) => Promise<Task[] | void>;
  /**
   * Take back a breakdown (§8.6.2), given the steps `onBreakdown` reported — which is why it
   * reports them. `restore` is present only when the breakdown was a split, which replaced a
   * step as well as creating some; a parent's own breakdown replaced nothing.
   */
  onUndoBreakdown?: (
    parent: Task,
    undo: {
      created: string[];
      restore?: { id: string; title: string; sortOrder: number };
    },
  ) => Promise<void>;
  /** Set `subtaskId` as `parent`'s Next Action explicitly (§8.6.1) — the manual override
   * next to the silent auto-advance on completion. */
  onSetNextAction?: (parent: Task, subtaskId: string) => Promise<void>;
  /** Persist a new sibling order under `parent` (§8.6, drag-to-reorder in the unfolded
   * list). */
  onReorderSubtasks?: (parent: Task, ids: string[]) => Promise<void>;
  /**
   * The id of whichever subtask most recently became a Next Action — §8.6.1's "brief
   * highlight", driven by the action that caused it rather than detected by a row watching
   * its own props change. A row compares its own id against this directly; nothing here
   * survives a remount to lose, which per-row change-detection did (D131 — a parent's own
   * group can change the instant its `last_touched_at` bubbles, moving its row into a
   * different section's DOM tree mid-interaction). `undefined` where nothing sets it
   * (`basicTaskActions`), same as any other row simply never flashing.
   */
  justPromotedId?: string | null;
};

export function TaskRow({
  task,
  today,
  areas = [],
  projects = [],
  actions,
  aiTiers = NO_AI_TIERS,
}: {
  task: Task;
  today: string;
  /** Only needed when `actions.onEdit` is set — the editor's area/project picker. */
  areas?: Area[];
  projects?: Project[];
  actions: TaskActions;
  /** What the AI-breakdown tier picker can currently offer (§11 item 9b) — computed once by
   * `TaskList` and threaded down here and into `SubtaskFold`, rather than each row fetching
   * it independently. Defaults to "nothing available," so the many direct `<TaskRow>` renders
   * in tests don't need to supply it just to get a row that renders no AI button at all. */
  aiTiers?: AiTierAvailability;
}) {
  const [completing, setCompleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [breakingDown, setBreakingDown] = useState(false);
  /** The breakdown just made, held so it can be taken back (§8.6.2). Owned here because
   * either half can start one: a parent's own form below, or a step's split inside the
   * fold — and both undo through the same control. */
  const [undoable, setUndoable] = useState<UndoableBreakdown>(null);
  const settings = useSettings();
  const done = task.status === "done";
  // The completion animation ends at opacity 0 and holds there (`forwards`). React reuses
  // this component for the row once it comes back as done, so leaving the class on would
  // keep an undoable row invisible — present, but impossible to find.
  const animating = completing && !done;

  // Nothing ever cleared `completing` after that, though — undo this same row later (status
  // flips back to `open`, same id) and it was still `true`, so `animating` above went right
  // back to `true` too and replayed the fade-to-invisible meant for the completion, on the
  // undo. Resetting whenever id or status changes covers both directions.
  useEffect(() => {
    setCompleting(false);
  }, [task.id, task.status]);

  if (editing && actions.onEdit) {
    return (
      <TaskEditor
        task={task}
        areas={areas}
        projects={projects}
        onCancel={() => setEditing(false)}
        onSave={async (patch, recurrence) => {
          await actions.onEdit!(task, patch, recurrence);
          setEditing(false);
        }}
        onDelete={actions.onDelete}
      />
    );
  }

  function complete(event: React.MouseEvent<HTMLButtonElement>) {
    setCompleting(true);
    // Where the burst should go: the row's midline, so it reads as coming from the thing
    // that was finished rather than from the button.
    const box = event.currentTarget.closest("li")!.getBoundingClientRect();
    const at = { x: box.left + Math.min(box.width * 0.4, 220), y: box.top + box.height / 2 };
    // Let the ~200ms animation play before the row leaves the list (§8.4).
    window.setTimeout(() => {
      void (async () => {
        try {
          await actions.onComplete(task, at);
        } finally {
          // Put the row back if it is still here and still open. The animation ends at
          // `opacity: 0` and holds there (`forwards`), and the effect above only clears
          // `completing` when the task's own id or status changes — so anything that
          // finishes without the task becoming done leaves a full-height invisible husk
          // where the row was. Reported exactly that way: "just replaced by empty space."
          // The live case is a completion queued offline (§8.2), which deliberately does
          // not mark the task done; a failed request is the same shape. Harmless when the
          // completion did land, since `done` is true by then and `animating` is false
          // regardless of this.
          setCompleting(false);
        }
      })();
    }, 200);
  }

  function dropTask() {
    if (
      window.confirm(
        `Drop "${task.title}"?\n\n` +
          "It leaves your lists but is not deleted - the history that mentions it " +
          "stays intact, and it can be found again with a status filter.",
      )
    ) {
      void actions.onDrop!(task);
    }
  }

  // Built regardless of `cardMode` — cheap, and it keeps the items list itself in one place
  // rather than duplicated between an inline block and a menu block. Same four actions as
  // Planning mode's inline buttons, same order, just reached through one more click.
  const overflowItems: OverflowMenuItem[] = [];
  if (actions.onDefer) {
    overflowItems.push({
      key: "snooze",
      label: "Snooze",
      icon: <Snooze />,
      onClick: () => void actions.onDefer!(task),
    });
  }
  if (actions.onEdit) {
    overflowItems.push({ key: "edit", label: "Edit", icon: <Pencil />, onClick: () => setEditing(true) });
  }
  if (actions.onDrop) {
    overflowItems.push({ key: "drop", label: "Drop", icon: <Trash />, onClick: dropTask });
  }
  if (!task.rrule && actions.onBreakdown) {
    overflowItems.push({
      key: "split",
      label: "Break down",
      icon: <Split />,
      onClick: () => setBreakingDown((v) => !v),
    });
  }

  return (
    <li
      className={`task${animating ? " completing" : ""}${done ? " done" : ""}`}
      data-testid="task"
    >
      <TaskGlow energy={task.energy} overdue={isOverdue(task.due, today)} important={task.important} />
      <button
        className="tick"
        onClick={
          task.rrule !== null ? undefined : done ? () => void actions.onUndo?.(task) : complete
        }
        disabled={task.rrule !== null || animating || (done && !actions.onUndo)}
        aria-label={
          task.rrule !== null
            ? `${task.title}: complete from the Routines card`
            : done
              ? `Undo: ${task.title}`
              : `Complete: ${task.title}`
        }
        title={
          task.rrule !== null
            ? "Complete or skip today's occurrence from the Routines card"
            : done
              ? "Done. Click to put it back."
              : "Complete"
        }
      >
        {animating || done ? "✓" : ""}
      </button>

      <span className="title">
        <span className="text">{task.title}</span>
        <span className="meta">
          {/*
            A recurring task's own occurrence is completed from the Recurring card (§8.7),
            not from here - this row's tick is for the task itself, which stays open. The
            badge is a plain-English reminder of the rule (§4.6.2), never the RRULE string.
          */}
          {task.rrule !== null && (
            <span
              className="badge repeat"
              title="Complete or skip today's occurrence from the Routines card"
            >
              <Repeat /> {recurrenceLabel(task.rrule)}
            </span>
          )}
          {/*
            §11's skim order: energy first, fixed-width, so it's the same shape to scan for
            every row before reading anything else. Important and Starred follow it as
            icon-only marks (no more "important"/"starred" text) - the icons already carry
            that meaning everywhere else they appear (the button below, `StarredCard`), so
            spelling it out here was the one place saying it twice.
          */}
          {!done && actions.onEdit ? (
            <EnergyBadgeButton
              task={task}
              onChange={(value) => actions.onEdit!(task, { energy: value })}
            />
          ) : (
            <span className="badge energy" title={energyTitle(task.energy)}>
              <Die value={task.energy} title={energyTitle(task.energy)} />
            </span>
          )}
          {task.important && (
            <span className="badge important" title="Important">
              <Important />
            </span>
          )}
          {task.starred && (
            <span className="badge star" title="Starred">
              <Star filled />
            </span>
          )}
          {/*
            Sits right after the fixed-width group above, close to the title, rather than
            further down with the rest - medium priority on its own (matters less once you
            already know what the short title means, more when you don't), so it belongs
            nearer the thing it's giving context *for*. The tooltip carries the project too
            when there is one: the badge itself never grows a second line of text for it.
          */}
          {task.area_name && (
            <span
              className="badge"
              title={task.project_name ? `${task.project_name} - in ${task.area_name}` : `Area: ${task.area_name}`}
            >
              {task.area_name}
            </span>
          )}
          {task.due && (
            <span
              className={`badge${isOverdue(task.due, today) ? " overdue" : ""}`}
              title={`${isOverdue(task.due, today) ? "Overdue" : "Due"} ${task.due}`}
            >
              <Calendar /> {relativeDateLabel(task.due, today)}
            </span>
          )}
          {/*
            Estimate through Snoozed: lower priority to look at, and - unlike Quiet/Seen/
            Pushed's own sort buttons, hidden alongside them (`TaskSortControls`'s own
            docstring) - nothing here is reachable any other way while collapsed, so hiding
            them in Execute mode loses nothing a control elsewhere still promises.
          */}
          {settings.cardMode === "plan" && (
            <>
              {task.estimate_min !== null && task.estimate_min > 0 && (
                <span className="badge" title="How long you thought this would take">
                  <Stopwatch /> {estimateLabel(task.estimate_min)}
                </span>
              )}
              {/* The pair reads as one signal. `chosen` is days you picked this for today;
                  `pushed` is days you moved it away. */}
              {task.starred_count > 0 && (
                <span className="badge chosen" title="Days you chose this for today">
                  chosen ×{task.starred_count}
                </span>
              )}
              {task.days_since_touch !== undefined && task.days_since_touch > 0 && (
                <span className="badge" title="Days since anything happened on this">
                  {task.days_since_touch}d quiet
                </span>
              )}
              {task.surfaced_count >= 3 && task.starred_count === 0 && (
                <span
                  className="badge"
                  title={`Shown on the briefing ${task.surfaced_count} separate days and not yet chosen`}
                >
                  seen ×{task.surfaced_count}
                </span>
              )}
              {task.deferred_count > 0 && (
                <span className="badge" title="Days you pushed this away">
                  pushed ×{task.deferred_count}
                </span>
              )}
              {task.snoozed_until && task.snoozed_until > today && (
                <span className="badge" title="Hidden from the list until this date">
                  <Snooze /> {task.snoozed_until}
                </span>
              )}
            </>
          )}
        </span>
      </span>

      {/*
        Wrapped so a narrow screen can move all of them to their own row at once. On
        desktop this span is `display: contents`, so the buttons stay direct flex children
        of the row and the layout is byte-for-byte what it was.
      */}
      <span className="task-actions">
        {/* Tick and Star are the two you reach for constantly — the only two buttons that
            never move behind the kebab, in either mode (§11). */}
        {!done && actions.onStar && (
          <button
            className="btn ghost small"
            onClick={() => void actions.onStar!(task)}
            aria-label={task.starred ? `Unstar: ${task.title}` : `Star: ${task.title}`}
            title={task.starred ? "Unstar" : "Star - one of today's few"}
          >
            <Star filled={task.starred} />
          </button>
        )}

        {/*
          Snooze, Edit, Drop and Break down: inline in Planning mode, the same buttons this
          row has always had. In Execute mode they move into the kebab just below instead -
          Snooze included, on the theory that a habit that hasn't formed with the button in
          plain sight isn't being held back by visibility, and one click away in the kebab
          (or a mode flip) costs nothing if it turns out it was.
        */}
        {!done && settings.cardMode === "plan" && (
          <>
            {actions.onDefer && (
              <button
                className="btn ghost small"
                onClick={() => void actions.onDefer!(task)}
                aria-label={`Snooze: ${task.title}`}
                title="Snooze - hide this until tomorrow"
              >
                <Snooze />
              </button>
            )}

            {actions.onEdit && (
              <button
                className="btn ghost small"
                onClick={() => setEditing(true)}
                aria-label={`Edit: ${task.title}`}
                title="Edit"
              >
                <Pencil />
              </button>
            )}

            {actions.onDrop && (
              <button
                className="btn ghost small"
                onClick={dropTask}
                aria-label={`Drop: ${task.title}`}
                title="Drop - not doing this"
              >
                <Trash />
              </button>
            )}

            {/* A recurring task can never have subtasks (§8.6), so the control never offers
                to break one down. */}
            {!task.rrule && actions.onBreakdown && (
              <button
                className="btn ghost small"
                onClick={() => setBreakingDown((v) => !v)}
                aria-label={`Break down: ${task.title}`}
                title="Break this into smaller steps"
              >
                <Split />
              </button>
            )}
          </>
        )}

        {!done && settings.cardMode === "execute" && (
          <OverflowMenu label={`More actions: ${task.title}`} items={overflowItems} />
        )}

        {/* Held back until Split is pressed, rather than sitting beside it permanently —
            two ways to break the same task down competing for attention on every row was
            more chrome than the row needed until the owner has actually said "break this
            down." */}
        {breakingDown && !done && !task.rrule && actions.onAiBreakdown && (
          <AiBreakdownButton task={task} tiers={aiTiers} onRun={actions.onAiBreakdown} />
        )}
      </span>

      {/*
        A sibling of `.title`/`.task-actions`, not nested inside `.title` - `.title` is only
        ever as wide as its own flex/grid column, which the actions cluster already needs
        the same trick to escape (see `.task > .clamp-wrap` in styles.css). Nesting this in
        `.title` instead reproduced the exact width bug the Notes card had before it moved
        its own body out of the title/badge column.
      */}
      {task.notes && (
        <ClampedText
          text={task.notes}
          maxLines={settings.taskNotesLines}
          textClassName="muted small task-notes"
        />
      )}

      {breakingDown && actions.onBreakdown && (
        <div className="clamp-wrap breakdown-wrap">
          <BreakdownForm
            placeholder="A step…"
            submitLabel="Confirm"
            onCancel={() => setBreakingDown(false)}
            onSubmit={async (titles) => {
              const created = await actions.onBreakdown!(task, titles);
              // Breaking a parent down is triggered here, not inside the fold — so the fold
              // is told about it, rather than discovering it (§8.6.2). Nothing was replaced,
              // so there is no `original`.
              if (created?.length) setUndoable({ created: created.map((c) => c.id) });
              setBreakingDown(false);
            }}
          />
        </div>
      )}

      {/* §8.6.4: subtasks are reachable only through their parent — this is that reach. */}
      {(task.subtask_total_count ?? 0) > 0 && (
        <div className="clamp-wrap">
          <SubtaskFold
            task={task}
            areas={areas}
            projects={projects}
            actions={actions}
            undoable={undoable}
            setUndoable={setUndoable}
            aiTiers={aiTiers}
          />
        </div>
      )}
    </li>
  );
}

export function TaskList({
  tasks,
  today,
  areas = [],
  projects = [],
  actions,
  empty,
}: {
  tasks: Task[];
  today: string;
  areas?: Area[];
  projects?: Project[];
  actions: TaskActions;
  empty?: string;
}) {
  // Called unconditionally, before the empty-list early return below — hooks can't be
  // conditional, and a fetch this cheap costs nothing on the empty-list path anyway.
  const aiTiers = useAiTierAvailability();

  if (tasks.length === 0) {
    return empty ? <p className="muted small">{empty}</p> : null;
  }
  return (
    <ul className="tasks">
      {tasks.map((task) => (
        <TaskRow
          key={task.id}
          task={task}
          today={today}
          areas={areas}
          projects={projects}
          actions={actions}
          aiTiers={aiTiers}
        />
      ))}
    </ul>
  );
}

/**
 * The same list, split into §8.5's groups. Each group is boxed so the boundary is visible
 * without reading the headings, and an empty group is omitted entirely rather than shown
 * as a heading with nothing under it.
 */
/** One group's heading, sort state and list — pulled out of `GroupedTaskList` so each of
 * the four groups gets its own `useState`, not one shared between all of them. Overdue
 * sorted by due date has no bearing on wanting Everything else sorted by how long it's
 * been quiet; they're different lists asking different questions. */
function TaskGroupSection({
  group,
  tasks,
  today,
  areas,
  projects,
  actions,
}: {
  group: TaskGroup;
  tasks: Task[];
  today: string;
  areas: Area[];
  projects: Project[];
  actions: TaskActions;
}) {
  const [sort, setSort] = useState<TaskSort>(INITIAL_SORT);
  const settings = useSettings();
  const label = GROUP_LABELS[group];
  const sorted = [...tasks].sort((a, b) => compareTasks(a, b, sort));

  return (
    <section className={`group group-${group}`} data-testid={`group-${group}`}>
      <div className="group-head">
        <h3>
          {label.title} · {sorted.length}
        </h3>
        {settings.cardMode === "plan" && <TaskSortControls sort={sort} onChange={setSort} />}
        {label.blurb && <span className="muted small">{label.blurb}</span>}
      </div>
      <TaskList
        tasks={sorted}
        today={today}
        areas={areas}
        projects={projects}
        actions={actions}
      />
    </section>
  );
}

export function GroupedTaskList({
  tasks,
  today,
  areas,
  projects,
  actions,
  empty,
}: {
  tasks: Task[];
  today: string;
  areas: Area[];
  projects: Project[];
  actions: TaskActions;
  empty?: string;
}) {
  if (tasks.length === 0) {
    return empty ? <p className="muted small">{empty}</p> : null;
  }

  return (
    <>
      {GROUP_ORDER.map((group) => {
        const inGroup = tasks.filter((t) => (t.group ?? "open") === group);
        if (inGroup.length === 0) return null;

        return (
          <TaskGroupSection
            key={group}
            group={group}
            tasks={inGroup}
            today={today}
            areas={areas}
            projects={projects}
            actions={actions}
          />
        );
      })}
    </>
  );
}
